/**
 * JanusPlayerAdapter handles WebRTC streaming using the Janus Gateway
 * for low-latency live streaming. It manages WebRTC peer connections,
 * stream handling, and integrates with the player interface for seamless playback.
 */

import { Logger, AnyFunc, createEventEmitter, EventEmitter, ErrorCodes, createError } from '@quickplay/foundation';
import {
  TrackInfo,
  PlaybackStatistics,
  RequestType,
  SeekRangeInfo,
  TrackVariantInfo,
  TrackType,
  ThumbnailPreviewMetadata,
  PlaybackOptions,
} from '@quickplay/player-interface';
import { PlayerAdapter } from '../../PlayerAdapter';
import { JanusErrorCodes } from '../../../JanusErrorCodes';

// Janus Gateway types (will be imported from janus.js library)
declare global {
  interface Window {
    Janus: any;
    adapter: any; // WebRTC adapter polyfill
  }
}

import { JanusConfig, JanusStreamInfo, JanusAuthConfig } from './JanusTypes';
import { PlayerErrorCodes } from '../../../PlayerErrorCodes';

export class JanusPlayerAdapter implements PlayerAdapter {
  // Constants for timing and configuration
  private static readonly TRACK_COLLECTION_DELAY_MS = 100;
  private static readonly PROGRESS_UPDATE_INTERVAL_MS = 250;

  private readonly emitter_: EventEmitter;
  private janus_: any = null;
  private streamingHandle_: any = null;
  private readonly videoElement_: HTMLVideoElement;
  private audioElement_: HTMLAudioElement | null = null;
  private readonly logger_: Logger;
  private readonly config_: JanusConfig;
  private isBuffering_ = false;
  private remoteStream_: MediaStream | null = null;
  private audioStream_: MediaStream | null = null;
  private playbackOptions_: PlaybackOptions;
  private isInitialized_ = false;
  private pendingVideoTracks_: MediaStreamTrack[] = [];
  private pendingAudioTracks_: MediaStreamTrack[] = [];
  private playTimeout_: number | null = null;
  private hasTracksReady_ = false;
  private playerStarted_ = false;
  private progressTimer_: number | null = null;
  private streamLoadPromise_: Promise<void> | null = null;
  private streamLoadResolve_: (() => void) | null = null;
  private streamLoadReject_: ((error: any) => void) | null = null;
  // Note: WebRTC doesn't use traditional HTTP request/response interceptors
  // These are required by PlayerAdapter interface but not applicable to WebRTC streaming

  constructor(videoElement: HTMLVideoElement, config: JanusConfig, playbackOptions: PlaybackOptions, logger: Logger) {
    this.videoElement_ = videoElement;
    this.config_ = config;
    this.playbackOptions_ = playbackOptions;
    this.logger_ = logger;
    this.emitter_ = createEventEmitter();

    // Janus initialization moved to load() to prevent race conditions
    this.logger_.debug('[JanusPlayerAdapter] Constructor complete - Janus will be initialized on load()');
  }

  private async initializeJanus(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Check if Janus library is loaded
        if (!window.Janus) {
          throw new Error('Janus library not loaded. Please include janus.js script.');
        }

        // Initialize Janus library with conditional debug mode
        window.Janus.init({
          debug: this.config_.enableDebug ? 'all' : false, // Enable debug only if enableDebug is set
          callback: (): void => {
            this.logger_.debug('[JanusPlayerAdapter] Janus library initialized successfully');

            // Now create the Janus session
            this.createJanusSession()
              .then(() => {
                resolve();
              })
              .catch(error => {
                reject(error instanceof Error ? error : new Error(String(error)));
              });
          },
          error: (error: any): void => {
            this.logger_.error('[JanusPlayerAdapter] Janus library initialization error:', error);
            reject(new Error(`Janus library initialization failed: ${error}`));
          },
        });
      } catch (error) {
        this.logger_.error('[JanusPlayerAdapter] Failed to initialize Janus library:', error);
        throw createError(
          JanusErrorCodes.JANUS_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
          'Failed to initialize Janus library',
          undefined,
          error instanceof Error ? error.message : String(error),
        );
      }
    });
  }

  private async createJanusSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Prepare Janus initialization options
      const janusOptions: any = {
        server: this.config_.server,
        iceServers: this.config_.iceServers,
        iceTransportPolicy: this.config_.iceTransportPolicy ?? 'undefined',
        iceRestart: this.config_.iceRestart ?? true,
        success: (): void => {
          this.logger_.debug('[JanusPlayerAdapter] Janus session created successfully');
          this.attachStreamingPlugin()
            .then(() => {
              resolve();
            })
            .catch(error => {
              reject(error instanceof Error ? error : new Error(String(error)));
            });
        },
        error: (error: any): void => {
          this.logger_.error('[JanusPlayerAdapter] Janus session creation error:', error);
          const sessionError = createError(
            JanusErrorCodes.JANUS_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
            'Failed to create Janus session',
            undefined,
            String(error),
          );
          this.emitter_.publish('error', sessionError);
          reject(sessionError); // eslint-disable-line @typescript-eslint/prefer-promise-reject-errors
        },
        destroyed: (): void => {
          this.logger_.debug('[JanusPlayerAdapter] Janus session destroyed');
        },
      };

      // Add authentication if configured
      if (this.config_.auth) {
        this.addAuthenticationHeaders(janusOptions);
      }

      // Create Janus session
      this.janus_ = new window.Janus(janusOptions);
    });
  }

  private addAuthenticationHeaders(janusOptions: any): void {
    if (!this.config_.auth) return;

    const auth: JanusAuthConfig = this.config_.auth;
    this.logger_.debug(`[JanusPlayerAdapter] Configuring authentication: ${auth.method}`);

    switch (auth.method) {
      case 'token':
        if (auth.token) {
          janusOptions.token = auth.token;
          this.logger_.debug('[JanusPlayerAdapter] Token authentication configured');
        }
        break;

      case 'basic':
        if (auth.username && auth.password) {
          // Basic auth is typically handled at HTTP level
          // For WebSocket, we might need to use custom headers
          const credentials = btoa(`${auth.username}:${auth.password}`);
          janusOptions.headers = {
            Authorization: `Basic ${credentials}`,
            ...auth.headers,
          };
          this.logger_.debug('[JanusPlayerAdapter] Basic authentication configured');
        }
        break;

      case 'shared_secret':
        if (auth.sharedSecret) {
          // Shared secret authentication typically uses HMAC
          // This would require additional implementation for HMAC signing
          janusOptions.sharedSecret = auth.sharedSecret;
          this.logger_.debug('[JanusPlayerAdapter] Shared secret authentication configured');
        }
        break;

      case 'custom':
        if (auth.headers) {
          janusOptions.headers = {
            ...janusOptions.headers,
            ...auth.headers,
          };
          this.logger_.debug('[JanusPlayerAdapter] Custom authentication headers configured');
        }
        break;

      default:
        this.logger_.warn(`[JanusPlayerAdapter] Unknown authentication method: ${auth.method}`);
    }
  }

  private attachStreamingPlugin(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.janus_) {
        reject(new Error('Janus session not available'));
        return;
      }

      this.janus_.attach({
        plugin: 'janus.plugin.streaming',
        opaqueId: `streaming-${Date.now()}`,
        success: (pluginHandle: any): void => {
          this.streamingHandle_ = pluginHandle;
          this.logger_.debug('[JanusPlayerAdapter] Streaming plugin attached successfully');
          resolve();
        },
        error: (error: any): void => {
          this.logger_.error('[JanusPlayerAdapter] Streaming plugin attach error:', error);
          const pluginError = createError(
            JanusErrorCodes.JANUS_PLUGIN_ATTACH_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
            'Failed to attach streaming plugin',
            undefined,
            String(error),
          );
          this.emitter_.publish('error', pluginError);
          reject(pluginError); // eslint-disable-line @typescript-eslint/prefer-promise-reject-errors
        },
        onmessage: (msg: any, jsep: any): void => {
          this.handlePluginMessage(msg, jsep);
        },
        onremotetrack: (track: MediaStreamTrack, mid: string, on: boolean): void => {
          this.handleRemoteTrack(track, mid, on);
        },
        onwebrtcstate: (state: string): void => {
          this.logger_.debug(`[JanusPlayerAdapter] WebRTC state: ${state}`);
          if (state === 'connected') {
            this.changeIsBuffering_(false);
          } else if (state === 'disconnected' || state === 'failed') {
            this.changeIsBuffering_(true);
            this.logger_.error(`[JanusPlayerAdapter] WebRTC connection ${state}`);
            const webrtcError = createError(
              JanusErrorCodes.WEBRTC_CONNECTION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
              `WebRTC connection ${state}`,
              undefined,
              `WebRTC state: ${state}`,
            );
            this.emitter_.publish('error', webrtcError);
          }
        },
        onhangup: (reason: string = 'Likely due to ICE connectivity issues'): void => {
          this.handleJanusHangup(reason);
        },
        oncleanup: (): void => {
          this.logger_.debug('[JanusPlayerAdapter] Plugin cleanup');
          this.remoteStream_ = null;
          // Clear video element on cleanup
          if (this.videoElement_) {
            this.videoElement_.srcObject = null;
          }
        },
      });
    });
  }

  private handlePluginMessage(msg: any, jsep: any): void {
    if (msg?.janus === 'hangup') {
      this.handleJanusHangup(msg.reason);
      return;
    }

    // Handle streaming plugin errors
    if (msg.streaming === 'event' && (msg.error || msg.error_code)) {
      this.handleStreamingPluginError(msg);
      return;
    }

    // Handle streaming status messages
    if (msg.streaming === 'event' && msg.result?.status) {
      this.handleStreamingStatus(msg.result.status);
    }

    // Handle JSEP (SDP) messages for WebRTC negotiation
    if (jsep) {
      this.handleJsepMessage(jsep);
    }
  }

  private handleJanusHangup(reason?: string): void {
    const reasonMessage = reason ? `Reason: ${reason}` : 'No additional details from Janus gateway';
    this.logger_.error(`[JanusPlayerAdapter] Janus hangup received - ${reasonMessage}`);
    if (this.videoElement_) {
      this.videoElement_.srcObject = null;
    }

    const hangupError = createError(
      JanusErrorCodes.WEBRTC_HANGUP_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
      'WebRTC connection hung up',
      undefined,
      reasonMessage,
    );

    this.emitter_.publish('error', hangupError);
  }

  private handleStreamingPluginError(msg: any): void {
    const errorCode = msg.error_code || 'UNKNOWN';
    const errorMessage = msg.error || 'Unknown streaming plugin error';
    const details = `Janus Plugin Error Code: ${errorCode}, Message: ${errorMessage}`;

    this.logger_.error(`[JanusPlayerAdapter] Streaming plugin error - ${details}`);

    const streamingError = createError(
      JanusErrorCodes.JANUS_PLUGIN_ERROR | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
      `Streaming plugin error (${errorCode}): ${errorMessage}`,
      undefined,
      details,
    );

    this.emitter_.publish('error', streamingError);
  }

  private handleStreamingStatus(status: string): void {
    switch (status) {
      case 'starting':
        // Stream is starting - no action needed
        break;
      case 'started':
        this.handleStreamStarted();
        break;
      case 'stopped':
        this.handleStreamStopped();
        break;
      default:
        this.logger_.debug(`[JanusPlayerAdapter] Unknown streaming status: ${status}`);
    }
  }

  private handleStreamStarted(): void {
    // Publish playerstarted when Janus reports the stream has started
    // This ensures the player state transitions from 'loaded' to 'started'
    if (!this.playerStarted_) {
      this.emitter_.publish('playerstarted');
      this.playerStarted_ = true;
    }

    // Resolve the load promise now that the stream is fully ready
    if (this.streamLoadResolve_) {
      this.streamLoadResolve_();
      this.streamLoadResolve_ = null;
      this.streamLoadReject_ = null;
      this.streamLoadPromise_ = null;
    }
  }

  private handleStreamStopped(): void {
    this.emitter_.publish('playerended');

    // If we were waiting for the stream to start and it stopped instead, reject the promise
    if (this.streamLoadReject_) {
      const stopError = createError(
        JanusErrorCodes.JANUS_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
        'Stream stopped before it could start',
        undefined,
        'Janus reported stream stopped during load process',
      );
      this.streamLoadReject_(stopError);
      this.streamLoadReject_ = null;
      this.streamLoadResolve_ = null;
      this.streamLoadPromise_ = null;
    }
  }

  private handleJsepMessage(jsep: any): void {
    this.logger_.debug('[JanusPlayerAdapter] Handling JSEP:', jsep.type);

    if (jsep.type === 'offer') {
      this.handleSdpOffer(jsep);
    } else if (jsep.type === 'answer') {
      this.handleSdpAnswer(jsep);
    }
  }

  private handleSdpOffer(jsep: any): void {
    // When watching a stream, Janus sends an SDP offer, we need to create an answer
    this.streamingHandle_.createAnswer({
      jsep: jsep,
      // We only specify data channels here, as this way in
      // case they were offered we'll enable them. Since we
      // don't mention audio or video tracks, we autoaccept them
      // as recvonly (since we won't capture anything ourselves)
      tracks: [{ type: 'data' }],
      success: (ourJsep: any): void => {
        this.logger_.debug('[JanusPlayerAdapter] Created answer, sending start request');
        this.streamingHandle_.send({
          message: { request: 'start' },
          jsep: ourJsep,
        });
      },
      error: (error: any): void => {
        this.logger_.error('[JanusPlayerAdapter] Failed to create answer:', error);
        const webrtcAnswerError = createError(
          JanusErrorCodes.WEBRTC_CONNECTION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
          'Failed to create WebRTC answer',
          undefined,
          String(error),
        );
        this.emitter_.publish('error', webrtcAnswerError);
      },
    });
  }

  private handleSdpAnswer(jsep: any): void {
    // Handle SDP answers (though for streaming plugin, we typically get offers)
    this.streamingHandle_.handleRemoteJsep({ jsep });
  }

  private handleRemoteTrack(track: MediaStreamTrack, mid: string, on: boolean): void {
    this.logger_.debug(`Remote track (mid=${mid}) ${on ? 'added' : 'removed'}:`, track);

    if (!on) {
      this.handleTrackRemoved(track, mid);
      return;
    }

    this.handleTrackAdded(track, mid);
  }

  private handleTrackRemoved(track: MediaStreamTrack, mid: string): void {
    this.logger_.debug(`[JanusPlayerAdapter] Track muted/removed for mid ${mid}`);

    if (track?.readyState === 'ended') {
      const reason = `MediaStreamTrack ended (mid=${mid}, kind=${track.kind || 'unknown'})`;
      this.handleJanusHangup(reason);
    } else {
      // For WebRTC, when onremotetrack is called with on=false, it means the track
      // is temporarily muted/not flowing, but the MediaStreamTrack is still valid.
      // We should NOT clear srcObject as it would interrupt ongoing playback.
      // The MediaStream will automatically handle the muted track.
    }
  }

  private handleTrackAdded(track: MediaStreamTrack, mid: string): void {
    if (track.kind === 'video') {
      this.handleVideoTrack(track, mid);
    } else if (track.kind === 'audio') {
      this.handleAudioTrack(track, mid);
    }

    this.emitter_.publish('trackschanged');
  }

  private handleVideoTrack(track: MediaStreamTrack, _mid: string): void {
    this.logger_.debug('[JanusPlayerAdapter] Video track received');
    // Collect video tracks for unified playback
    this.pendingVideoTracks_.push(track);

    // Schedule unified playback setup
    this.schedulePlaybackSetup();
  }

  private handleAudioTrack(track: MediaStreamTrack, _mid: string): void {
    this.logger_.debug('[JanusPlayerAdapter] Audio track received');
    // Collect audio tracks for unified playback (like video tracks)
    this.pendingAudioTracks_.push(track);

    // Schedule unified playback setup
    this.schedulePlaybackSetup();
  }

  private schedulePlaybackSetup(): void {
    // Clear any existing timeout
    if (this.playTimeout_) {
      clearTimeout(this.playTimeout_);
    }

    // Schedule playback setup after a short delay to collect all tracks
    this.playTimeout_ = window.setTimeout(() => {
      this.setupPlaybackWithAllTracks();
    }, JanusPlayerAdapter.TRACK_COLLECTION_DELAY_MS); // Delay to collect tracks
  }

  private setupPlaybackWithAllTracks(): void {
    // Check if we have a video element and at least one track (either video or audio)
    if (!this.videoElement_ || (this.pendingVideoTracks_.length === 0 && this.pendingAudioTracks_.length === 0)) {
      this.logger_.debug('[JanusPlayerAdapter] No video element or no tracks available for playback setup');
      return;
    }

    this.logger_.debug(
      `[JanusPlayerAdapter] Setting up playback with ${this.pendingVideoTracks_.length} video tracks and ${this.pendingAudioTracks_.length} audio tracks`,
    );

    // Handle video tracks if available
    if (this.pendingVideoTracks_.length > 0) {
      this.setupVideoPlayback();
    } else {
      this.logger_.debug('[JanusPlayerAdapter] No video tracks available - audio-only stream');
    }

    // Handle audio tracks if available (can work with separate audio element)
    if (this.pendingAudioTracks_.length > 0) {
      this.setupAudioPlayback();
    } else {
      this.logger_.debug('[JanusPlayerAdapter] No audio tracks available - video-only stream');
    }

    // Clear pending tracks
    this.pendingVideoTracks_ = [];
    this.pendingAudioTracks_ = [];

    // Mark that we have tracks ready for playback
    this.hasTracksReady_ = true;
    this.logger_.debug('[JanusPlayerAdapter] Tracks are ready for playback');
  }

  private setupVideoPlayback(): void {
    // Create a MediaStream with video tracks
    const videoStream = new MediaStream(this.pendingVideoTracks_);
    this.remoteStream_ = videoStream;

    this.logger_.debug(`[JanusPlayerAdapter] Setting up video playback with ${this.pendingVideoTracks_.length} tracks`);
    this.logger_.debug(`[JanusPlayerAdapter] Video element is in DOM: ${document.contains(this.videoElement_)}`);
    this.logger_.debug(
      `[JanusPlayerAdapter] Video element dimensions: ${this.videoElement_.offsetWidth}x${this.videoElement_.offsetHeight}`,
    );

    // Use Janus helper to attach media stream
    if (window.Janus?.attachMediaStream) {
      window.Janus.attachMediaStream(this.videoElement_, videoStream);
    } else {
      this.videoElement_.srcObject = videoStream;
    }

    // Configure video element for proper playback
    this.videoElement_.autoplay = true;
    this.videoElement_.muted = false; // Allow audio playback (though audio will come from separate element)
    this.videoElement_.playsInline = true; // For mobile devices
  }

  private setupAudioPlayback(): void {
    // Create a MediaStream with audio tracks
    const audioStream = new MediaStream(this.pendingAudioTracks_);
    this.audioStream_ = audioStream;

    this.logger_.debug(`[JanusPlayerAdapter] Setting up audio playback with ${this.pendingAudioTracks_.length} tracks`);

    // If we have no video tracks, use the video element for audio playback as well
    // This provides better integration with the player interface
    if (this.pendingVideoTracks_.length === 0) {
      this.logger_.debug('[JanusPlayerAdapter] Audio-only stream detected, using video element for audio playback');

      // Use the video element for audio-only playback
      if (window.Janus?.attachMediaStream) {
        window.Janus.attachMediaStream(this.videoElement_, audioStream);
      } else {
        this.videoElement_.srcObject = audioStream;
      }

      // Configure video element for audio-only playback
      this.videoElement_.autoplay = true;
      this.videoElement_.muted = false;
      this.videoElement_.playsInline = true;

      // Store reference to the audio stream as remote stream for consistency
      this.remoteStream_ = audioStream;
    } else {
      // We have video tracks, so use a separate hidden audio element
      this.setupSeparateAudioElement(audioStream);
    }
  }

  private setupSeparateAudioElement(audioStream: MediaStream): void {
    // Create a hidden audio element for audio playback when video is also present
    if (!this.audioElement_) {
      this.audioElement_ = document.createElement('audio');
      this.audioElement_.style.display = 'none'; // Hidden like in streaming.js
      this.audioElement_.setAttribute('playsinline', ''); // For mobile devices
      // Add to DOM temporarily for playback
      document.body.appendChild(this.audioElement_);
    }

    this.logger_.debug('[JanusPlayerAdapter] Setting up separate audio element for audio+video stream');

    // Use Janus helper to attach audio stream to separate audio element
    if (window.Janus?.attachMediaStream) {
      window.Janus.attachMediaStream(this.audioElement_, audioStream);
    } else {
      this.audioElement_.srcObject = audioStream;
    }
  }

  private startProgressUpdates(): void {
    // Clear any existing timer
    this.stopProgressUpdates();

    // Start emitting progress updates every PROGRESS_UPDATE_INTERVAL_MS while playing
    this.progressTimer_ = window.setInterval(async () => {
      if (this.videoElement_ && !this.videoElement_.paused && !this.videoElement_.ended) {
        await this.emitProgressUpdate();
      }
    }, JanusPlayerAdapter.PROGRESS_UPDATE_INTERVAL_MS); // Interval for smooth progress updates
  }

  private stopProgressUpdates(): void {
    if (this.progressTimer_) {
      clearInterval(this.progressTimer_);
      this.progressTimer_ = null;
    }
  }

  private async emitProgressUpdate(): Promise<void> {
    if (!this.videoElement_) return;

    // For live streams, duration is typically 0 or Infinity
    // currentTime represents how long we've been playing
    const currentTime = this.videoElement_.currentTime;
    const duration = 0; // this.videoElement_.duration

    // Get RTP timestamp from WebRTC stats for epochTime
    let epochTime = 0;
    try {
      if (this.streamingHandle_?.webrtcStuff?.pc) {
        const pc = this.streamingHandle_.webrtcStuff.pc;
        const stats = await pc.getStats();
        stats.forEach((report: any) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            // Use remote timestamp (NTP time from Janus Sender Report in ms since epoch)
            epochTime = report.remoteTimestamp / 1000 || report.timestamp / 1000 || 0;
          }
        });
      }
    } catch (error) {
      this.logger_.debug('[JanusPlayerAdapter] Failed to get WebRTC stats for epochTime:', error);
    }

    this.emitter_.publish('timeupdate', currentTime, duration, epochTime);
  }

  private changeIsBuffering_(buffering: boolean): void {
    if (buffering !== this.isBuffering_) {
      this.isBuffering_ = buffering;
      const eventName = buffering ? 'bufferingstart' : 'bufferingend';
      this.emitter_.publish(eventName);
    }
  }

  // PlayerAdapter interface implementation

  public get internalPlayerName(): string {
    return 'janus-webrtc-player';
  }

  public get internalPlayerVersion(): string {
    return 'unknown';
  }

  public get rawLibrary(): any {
    return window.Janus;
  }

  public get rawPlayer(): any {
    return this.streamingHandle_;
  }

  public get clockSyncUri(): string {
    return '';
  }

  public get minBuffer(): number {
    return 0; // WebRTC doesn't use traditional buffering
  }

  public get maxBuffer(): number {
    return 0; // WebRTC doesn't use traditional buffering
  }

  public get playbackRate(): number {
    return this.videoElement_?.playbackRate || 1;
  }

  public get bufferBehind(): number {
    return 0; // Not applicable for WebRTC
  }

  public get availableBitrates(): number[] {
    return []; // WebRTC bitrate is managed by the browser
  }

  public get maxBitrate(): number {
    return 0; // Not applicable for WebRTC
  }

  public get minBitrate(): number {
    return 0; // Not applicable for WebRTC
  }

  public get playbackStatistics(): PlaybackStatistics {
    return Object.freeze({
      bufferRange: '0',
      decodedFrames: 0,
      droppedFrames: 0,
      corruptedFrames: 0,
      playTime: 0,
      pauseTime: 0,
      licenseTime: 0,
      bufferingTime: 0,
      requiredBandwidth: 0,
      estimatedBandwidth: 0,
      variantBitrate: 0,
      videoBitrate: 0,
      audioBitrate: 0,
      loadLatency: 0,
      width: this.videoElement_?.videoWidth || 0,
      height: this.videoElement_?.videoHeight || 0,
      framerate: 0,
    }) as PlaybackStatistics;
  }

  public get isLive(): boolean {
    return true;
  }

  public get isMute(): boolean {
    // For audio-only streams, audio is played through the video element
    // For video+audio streams, audio is played through the separate audio element
    if (this.audioElement_) {
      return this.audioElement_.muted;
    }
    // If no separate audio element, check the video element (audio-only case)
    return this.videoElement_?.muted ?? true;
  }

  public get playbackOptions(): PlaybackOptions {
    return this.playbackOptions_;
  }

  public get volume(): number {
    // For audio-only streams, audio is played through the video element
    // For video+audio streams, audio is played through the separate audio element
    if (this.audioElement_) {
      return this.audioElement_.volume;
    }
    // If no separate audio element, return volume from video element (audio-only case)
    return this.videoElement_?.volume ?? 1;
  }

  public set volume(volumeLevel: number) {
    // Always set volume on video element (handles both video+audio and audio-only cases)
    if (this.videoElement_) {
      this.videoElement_.volume = volumeLevel;
    }
    // Also set on separate audio element if it exists (video+audio case)
    if (this.audioElement_) {
      this.audioElement_.volume = volumeLevel;
    }
  }

  private extractTracksFromStream(stream: MediaStream, tracks: TrackInfo[]): void {
    stream.getVideoTracks().forEach(() => {
      if (!tracks.some(t => t.type === TrackType.VIDEO)) {
        tracks.push({
          type: TrackType.VIDEO,
          language: 'und',
        });
      }
    });

    stream.getAudioTracks().forEach(() => {
      if (!tracks.some(t => t.type === TrackType.AUDIO)) {
        tracks.push({
          type: TrackType.AUDIO,
          language: 'und',
        });
      }
    });
  }

  public getAllTracks(): TrackInfo[] {
    const tracks: TrackInfo[] = [];

    // Check the remote video stream
    if (this.remoteStream_) {
      this.extractTracksFromStream(this.remoteStream_, tracks);
    }

    // Check the audio stream
    if (this.audioStream_) {
      this.extractTracksFromStream(this.audioStream_, tracks);
    }

    // Check the video element's current stream
    if (this.videoElement_?.srcObject && this.videoElement_.srcObject !== this.remoteStream_) {
      const elementStream = this.videoElement_.srcObject as MediaStream;
      this.extractTracksFromStream(elementStream, tracks);
    }

    return tracks;
  }

  public getActiveTracks(): TrackInfo[] {
    return this.getAllTracks(); // All tracks are active in WebRTC
  }

  public getVariantTracks(): TrackVariantInfo[] {
    return []; // WebRTC doesn't have variant tracks like DASH/HLS
  }

  public getAvailableTrackVariants(_trackType: TrackType): TrackVariantInfo[] {
    return []; // WebRTC doesn't have variant tracks
  }

  public selectTrack(_track?: TrackInfo): void {
    // WebRTC doesn't support track selection like traditional streaming
    this.logger_.debug('[JanusPlayerAdapter] Track selection not supported in WebRTC');
  }

  public setRequestInterceptor(_interceptor: (requestType: RequestType, request: any) => void): void {
    // WebRTC doesn't use traditional HTTP requests, so interceptors are not applicable
    this.logger_.debug('[JanusPlayerAdapter] Request interceptor not applicable for WebRTC streaming');
  }

  public setResponseInterceptor(_interceptor: (requestType: RequestType, response: any) => void): void {
    // WebRTC doesn't use traditional HTTP responses, so interceptors are not applicable
    this.logger_.debug('[JanusPlayerAdapter] Response interceptor not applicable for WebRTC streaming');
  }

  public async load(_initialPlaybackTime?: number): Promise<void> {
    try {
      this.logger_.debug('[JanusPlayerAdapter] Loading streams...');

      // Initialize Janus if not already done
      await this.ensureJanusInitialized();

      // If streamId is provided, bypass stream list and connect directly
      if (this.config_.streamId) {
        this.logger_.debug(`[JanusPlayerAdapter] Connecting directly to provided streamId: ${this.config_.streamId}`);
        return this.connectToStreamAndWaitForStart(this.config_.streamId);
      }

      // Get and validate available streams
      const streams = await this.getAvailableStreams();
      this.validateStreamsAvailable(streams);

      // Select and connect to stream
      const selectedStream = this.selectStream(streams);
      return this.connectToStreamAndWaitForStart(selectedStream.id);
    } catch (error) {
      this.logger_.error('[JanusPlayerAdapter] Failed to load stream:', error);
      throw error;
    }
  }

  private async ensureJanusInitialized(): Promise<void> {
    if (!this.isInitialized_) {
      await this.initializeJanus();
      this.isInitialized_ = true;
    }
  }

  private validateStreamsAvailable(streams: JanusStreamInfo[]): void {
    if (!streams || streams.length === 0) {
      throw createError(
        JanusErrorCodes.JANUS_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
        'No streams available from Janus server',
        undefined,
        'Stream list is empty or undefined',
      );
    }
  }

  private selectStream(streams: JanusStreamInfo[]): JanusStreamInfo {
    const firstStream = streams[0];
    this.logger_.debug(
      `[JanusPlayerAdapter] Auto-selecting first available stream: ${firstStream.id} (${firstStream.description})`,
    );
    return firstStream;
  }

  private async connectToStreamAndWaitForStart(streamId: number): Promise<void> {
    // Create a deferred promise that will resolve when Janus reports the stream is fully started
    this.streamLoadPromise_ = new Promise<void>((resolve, reject) => {
      this.streamLoadResolve_ = resolve;
      this.streamLoadReject_ = reject;
    });

    // Connect to the selected stream (similar to startStream in streaming.js)
    // Note: connectToStream resolves immediately on watch request success,
    // but we need to wait for Janus to report 'started' status
    try {
      await this.connectToStream(streamId);
    } catch (error) {
      // If connectToStream fails, reject the promise and clean up
      if (this.streamLoadReject_) {
        this.streamLoadReject_(error);
        this.streamLoadReject_ = null;
      }
      this.streamLoadPromise_ = null;
      throw error;
    }

    // Return the promise - it will resolve when Janus reports 'started'
    return this.streamLoadPromise_;
  }

  public async play(): Promise<void> {
    this.logger_.debug('[JanusPlayerAdapter] Play requested');

    // Check if we have tracks ready
    if (!this.hasTracksReady_) {
      this.logger_.debug('[JanusPlayerAdapter] No tracks ready yet, waiting...');
      // Could implement a promise that resolves when tracks are ready
      // For now, just try to play anyway
    }

    // Play video and audio elements
    await this.playVideoElement();
    this.playAudioElement();

    // Start emitting progress updates during playback
    this.startProgressUpdates();
    // Note: playerstarted event is now published when Janus reports stream started
  }

  private async playVideoElement(): Promise<void> {
    if (!this.videoElement_) return;

    try {
      const videoPlayPromise = this.videoElement_.play();
      if (videoPlayPromise !== undefined) {
        await videoPlayPromise;
        this.logger_.debug('[JanusPlayerAdapter] Video playback started successfully');
      } else {
        this.logger_.debug('[JanusPlayerAdapter] Video play() returned undefined (already playing?)');
      }
    } catch (error) {
      this.logger_.error('[JanusPlayerAdapter] Failed to play video:', error);
      throw createError(
        PlayerErrorCodes.MEDIA_PLAYBACK_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
        'Failed to start video playback',
        undefined,
        error instanceof Error ? error.message : String(error),
      );
    }
  }

  private playAudioElement(): void {
    if (!this.audioElement_) return;

    try {
      const audioPlayPromise = this.audioElement_.play();
      if (audioPlayPromise !== undefined) {
        audioPlayPromise
          .then(() => {
            this.logger_.debug('[JanusPlayerAdapter] Audio playback started successfully');
          })
          .catch(error => {
            this.logger_.error('[JanusPlayerAdapter] Failed to play audio:', error);
            // Don't throw here - video might still work
          });
      } else {
        this.logger_.debug('[JanusPlayerAdapter] Audio play() returned undefined (already playing?)');
      }
    } catch (error) {
      this.logger_.error('[JanusPlayerAdapter] Failed to play audio:', error);
      // Don't throw here - video might still work
    }
  }

  public pause(): void {
    if (this.videoElement_) {
      this.videoElement_.pause();
      this.emitter_.publish('playerpaused');
    }

    // Pause audio element
    if (this.audioElement_) {
      this.audioElement_.pause();
    }

    // Stop progress updates when paused
    this.stopProgressUpdates();
  }

  public stop(): void {
    if (this.streamingHandle_) {
      this.streamingHandle_.send({
        message: { request: 'stop' },
        success: (): void => {
          this.logger_.debug('[JanusPlayerAdapter] Stream stopped');
          this.emitter_.publish('playerended');
        },
        error: (error: any): void => {
          this.logger_.error('[JanusPlayerAdapter] Failed to stop stream:', error);
        },
      });
    }

    // Stop video element
    if (this.videoElement_) {
      this.videoElement_.pause();
      this.videoElement_.srcObject = null;
    }

    // Stop and clean up audio element
    if (this.audioElement_) {
      this.audioElement_.pause();
      this.audioElement_.srcObject = null;
      // Remove from DOM
      this.audioElement_.remove();
      this.audioElement_ = null;
    }

    // Stop progress updates
    this.stopProgressUpdates();

    this.hasTracksReady_ = false;
    this.pendingVideoTracks_ = [];
    this.pendingAudioTracks_ = [];
    this.playerStarted_ = false; // Reset for next play cycle
    if (this.playTimeout_) {
      clearTimeout(this.playTimeout_);
      this.playTimeout_ = null;
    }

    // Clean up any pending load promise if we're stopping during load
    if (this.streamLoadReject_) {
      this.streamLoadReject_(new Error('Player stopped during load'));
      this.streamLoadReject_ = null;
      this.streamLoadResolve_ = null;
      this.streamLoadPromise_ = null;
    }
  }

  public muteOrUnmute(): void {
    const newMuteState = !this.isMute;

    // For audio-only streams, mute/unmute the video element
    // For video+audio streams, mute/unmute both elements to maintain consistency
    if (this.audioElement_) {
      // Video+audio case: mute/unmute both the separate audio element and video element
      this.audioElement_.muted = newMuteState;
      if (this.videoElement_) {
        this.videoElement_.muted = newMuteState;
      }
    } else if (this.videoElement_) {
      // Audio-only case: audio is played through video element
      this.videoElement_.muted = newMuteState;
    }
  }

  public seek(_seekPosition: number): void {
    // WebRTC streams are live and don't support seeking
    this.logger_.debug('[JanusPlayerAdapter] Seek not supported for live WebRTC streams');
  }

  public seekToLiveEdge(): void {
    // Already at live edge for WebRTC streams
    this.logger_.debug('[JanusPlayerAdapter] Already at live edge');
  }

  public async unload(): Promise<void> {
    this.stop();

    if (this.streamingHandle_) {
      this.streamingHandle_.detach();
      this.streamingHandle_ = null;
    }

    if (this.janus_) {
      this.janus_.destroy();
      this.janus_ = null;
    }

    // Stop progress updates
    this.stopProgressUpdates();

    this.remoteStream_ = null;
    this.audioStream_ = null;
    this.hasTracksReady_ = false;
    this.playerStarted_ = false; // Reset for next load cycle
    this.pendingVideoTracks_ = [];
    this.pendingAudioTracks_ = [];
    if (this.playTimeout_) {
      clearTimeout(this.playTimeout_);
      this.playTimeout_ = null;
    }

    // Clean up any pending load promise
    if (this.streamLoadReject_) {
      this.streamLoadReject_(new Error('Player unloaded during load'));
      this.streamLoadReject_ = null;
      this.streamLoadResolve_ = null;
      this.streamLoadPromise_ = null;
    }
  }

  public seekableRange(): SeekRangeInfo {
    // WebRTC live streams don't have seekable ranges
    return {
      start: 0,
      end: 0,
    };
  }

  public getLiveEdge(): number {
    // For live streams, current time is the live edge
    return this.videoElement_?.currentTime || 0;
  }

  public currentOffsetFromLiveEdge(): number {
    // WebRTC is always at live edge
    return 0;
  }

  public setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.playbackOptions_ = playbackOptions;
  }

  public restrictQuality(_enableRestriction: boolean): void {
    // Not applicable for WebRTC
  }

  public async getThumbnail(_position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    // Thumbnails not supported for WebRTC streams
    return undefined;
  }

  public availableMimeTypes(_trackType: TrackType): Array<string> {
    // WebRTC uses standard WebRTC codecs
    return ['video/H264', 'audio/opus'];
  }

  public setPreferredMimeTypes(_trackType: TrackType, _mimeTypes: Array<string>): void {
    // Not applicable for WebRTC
  }

  public unloadAndSavePreload(): Promise<void> {
    // For now, just unload
    return this.unload();
  }

  public restoreFromPreload(): void {
    // For now, no preload restoration
    this.logger_.debug('[JanusPlayerAdapter] Preload restoration not implemented');
  }

  public subscribe(eventName: string, callback: AnyFunc): void {
    this.emitter_.subscribe(eventName, callback);
  }

  public unsubscribe(eventName: string, callback: AnyFunc): void {
    this.emitter_.unsubscribe(eventName, callback);
  }

  // Additional Janus-specific methods

  private async getAvailableStreams(): Promise<JanusStreamInfo[]> {
    return new Promise((resolve, reject) => {
      if (!this.streamingHandle_) {
        const pluginError = createError(
          JanusErrorCodes.JANUS_PLUGIN_ATTACH_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
          'Streaming plugin not attached',
          undefined,
          'Cannot retrieve streams without an attached streaming plugin',
        );
        reject(pluginError); // eslint-disable-line @typescript-eslint/prefer-promise-reject-errors
        return;
      }

      this.streamingHandle_.send({
        message: { request: 'list' },
        success: (result: any): void => {
          const streams: JanusStreamInfo[] = result.list || [];
          resolve(streams);
        },
        error: (error: any): void => {
          this.logger_.error('[JanusPlayerAdapter] Failed to get stream list:', error);
          const streamListError = createError(
            JanusErrorCodes.JANUS_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
            'Failed to retrieve available streams from Janus server',
            undefined,
            String(error),
          );
          reject(streamListError); // eslint-disable-line @typescript-eslint/prefer-promise-reject-errors
        },
      });
    });
  }

  public async connectToStream(streamId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.streamingHandle_) {
        const pluginError = createError(
          JanusErrorCodes.JANUS_PLUGIN_ATTACH_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
          'Streaming plugin not attached',
          undefined,
          'Cannot connect to stream without an attached streaming plugin',
        );
        reject(pluginError); // eslint-disable-line @typescript-eslint/prefer-promise-reject-errors
        return;
      }

      // Request to watch the stream
      const watchRequest = {
        request: 'watch',
        id: streamId,
      };

      this.streamingHandle_.send({
        message: watchRequest,
        success: (result: any): void => {
          this.logger_.debug('[JanusPlayerAdapter] Watch request successful:', result);
          resolve();
        },
        error: (error: any): void => {
          this.logger_.error('[JanusPlayerAdapter] Watch request failed:', error);
          const connectError = createError(
            JanusErrorCodes.JANUS_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_PLAYBACK,
            'Failed to connect to stream',
            undefined,
            String(error),
          );
          reject(connectError); // eslint-disable-line @typescript-eslint/prefer-promise-reject-errors
        },
      });
    });
  }
}
